/* Teeinblue × AMP Slide Cart — personalised line-item thumbnails.
 *
 * Slide Cart renders <img src="{line.image}"> (the plain product photo) and never
 * looks at line-item properties, and React re-uses those <img> nodes when the cart
 * re-renders. This reads Teeinblue's `_customization_image` out of the cart Slide
 * Cart already holds in memory (SLIDECART_STATE) inside the MutationObserver
 * callback — i.e. in the same microtask the line is written to the DOM, and
 * therefore before the browser paints it.
 *
 * IMPORTANT: this must be the only script touching the Slide Cart thumbnails.
 */
(function () {
  'use strict';

  // Floor on how long the `.is-loading` state stays up, measured from the moment
  // it first went up for a given design — not per attempt. A design already in
  // the browser cache is swapped in with no loading state at all.
  var MIN_LOADING_MS = 700;

  var MAX_LOADING_MS = 10000;  // fail-safe: give up on a design after this
  var RETRIES        = 3;      // Teeinblue's render is often not on the CDN yet
  var RETRY_MS       = 1500;

  var ready = {}, failed = {}, loading = {}, tries = {}, cool = {}, since = {};

  // FIX 19.08.2026 (3): Standard-Thumbnail pro Varianten-ID, sobald das Script es
  // einmal im DOM sieht. Beim Node-Recycling steht dadurch wieder das Produktfoto
  // unter der Ladeanimation statt eines leeren Feldes.
  var stock = {};
  var root = null;

  // Returns the design URL, null if the line is genuinely not personalised, or
  // undefined if Slide Cart's in-memory cart doesn't describe this line right now.
  // The undefined case matters: the state lags the DOM across Slide Cart's own
  // /cart.js refetches, and treating that as "not personalised" puts the plain
  // photo back with nothing left to trigger a correction.
  function designFor(key) {
    var s = window.SLIDECART_STATE && window.SLIDECART_STATE();
    var items = s && s.cart && s.cart.items;
    if (!items) return undefined;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) {
        var p = items[i].properties;
        return (p && p._customization_image) || null;
      }
    }
    return undefined;
  }

  function ensure(url) {
    if (ready[url]) return 'ready';
    if (loading[url]) return 'pending';
    if (failed[url]) return 'failed';
    if (cool[url] > Date.now()) return 'pending';  // between retries: keep loading up

    var probe = new Image();
    probe.src = url;

    // Already decoded in the browser cache: use it now, no loading state at all.
    if (probe.complete && probe.naturalWidth > 0) {
      ready[url] = true;
      return 'ready';
    }

    loading[url] = probe;
    if (!since[url]) since[url] = Date.now();

    var loaded = false, waited = false, settled = false;

    function settle(ok, giveUp) {
      if (settled) return;
      settled = true;
      delete loading[url];
      if (ok) {
        ready[url] = true;
      } else {
        tries[url] = (tries[url] || 0) + 1;
        if (giveUp || tries[url] >= RETRIES) failed[url] = true;
        else {
          cool[url] = Date.now() + RETRY_MS;
          setTimeout(scan, RETRY_MS + 50);
        }
      }
      scan();
    }

    probe.onload  = function () { loaded = true; if (waited) settle(true); };
    probe.onerror = function () { settle(false); };

    setTimeout(function () { waited = true; if (loaded) settle(true); },
               Math.max(0, MIN_LOADING_MS - (Date.now() - since[url])));
    setTimeout(function () { settle(loaded, true); }, MAX_LOADING_MS);

    return 'pending';
  }

  function veil(anchor, on) {
    var has = anchor.classList.contains('is-loading');
    if (on && !has) anchor.classList.add('is-loading');
    else if (!on && has) anchor.classList.remove('is-loading');
  }

  function scan() {
    if (!root) return;

    var lines = root.querySelectorAll('.item[data-line-item-key]');

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var anchor = line.querySelector('.item-image-anchor');
      var img = anchor && anchor.querySelector('img');
      if (!img) continue;

      var key = line.getAttribute('data-line-item-key');

      var variant = line.getAttribute('data-variant-id') || '';
      var shown = img.getAttribute('src') || '';

      // Standard-Thumbnail merken, solange es noch im Node steht.
      if (variant && shown && shown.indexOf('teeinblue') === -1 &&
          shown.indexOf('data:') === -1 && !stock[variant]) {
        stock[variant] = shown;
      }


      // FIX 19.08.2026: React recycelt <img>-Nodes zwischen Zeilen. Gehören die
      // gemerkten Werte noch zu einer anderen Line, vorher verwerfen — sonst
      // landet das Bild der alten Zeile auf der neuen.
      if (img.tibKey !== key) {
        // FIX 19.08.2026 (2+3): Slide Cart rendert die neue Zeile mit dem recycelten
        // <img> — darin steht noch das Design der alten Zeile. Standardfoto der
        // Variante zurücksetzen; falls unbekannt, transparentes Pixel als Notbremse.
        if (img.tibApplied) {
          // FIX 20.08.2026: zusätzlich tibBase als zweite Rückfallebene, und
          // Ladeanimation oben lassen — sonst wirkt das Standardfoto wie das
          // fertige Ergebnis, solange der Cart-Zustand die neue Zeile noch nicht kennt.
          img.setAttribute('src', stock[variant] || img.tibBase ||
            'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');
          veil(anchor, true);
        }
        img.tibKey = key;
        img.tibBase = null;
        img.tibApplied = null;
      }

      var url = designFor(key);
      if (url === undefined) continue;   // state can't tell us — leave the node alone

      var state = url ? ensure(url) : 'none';

      // Plain line, or a design we've given up on: hand the node back to Slide Cart.
      if (state === 'none' || state === 'failed') {
        if (img.tibApplied) {
          if (img.tibBase) img.setAttribute('src', img.tibBase);
          img.tibApplied = null;
        }
        veil(anchor, false);
        continue;
      }

      // Remember whatever Slide Cart itself rendered, so we can restore it later.
      var current = img.getAttribute('src') || '';
      // FIX 20.08.2026: nur echte Produktfotos merken — sonst landet das leere
      // Platzhalter-Pixel als "Standardfoto" im Gedächtnis.
      if (current !== img.tibApplied && current.indexOf('teeinblue') === -1 &&
          current.indexOf('data:') !== 0) img.tibBase = current;

      if (state === 'ready') {
        if (current !== url) img.setAttribute('src', url);
        img.tibApplied = url;
        veil(anchor, false);
      } else {
        veil(anchor, true);
      }
    }
  }

  function start(el) {
    root = el;
    // FIX 19.08.2026: Fehlversuche zurücksetzen, sobald der Drawer geöffnet wird.
    // Sonst bleibt ein bei schlechtem Netz gescheitertes Design bis zum
    // Seiten-Reload auf "kaputt" und zeigt dauerhaft das Standardfoto.
    var overlay = root.querySelector('.slidecarthq-overlay');
    if (overlay) {
      new MutationObserver(function () {
        if (overlay.classList.contains('open')) {
          failed = {}; tries = {}; cool = {}; since = {};
          scan();
        }
      }).observe(overlay, { attributes: true, attributeFilter: ['class'] });
    }
    // `src` only — never `class`: veil() would otherwise feed the observer and loop.
    // Our own src writes do trigger it, but the next scan finds nothing to change.
    new MutationObserver(scan).observe(root, {
      childList: true,
      subtree: true,
      attributeFilter: ['src']
    });
    scan();
  }

  function boot() {
    var el = document.getElementById('slidecarthq');
    if (el) return start(el);
    var waiter = new MutationObserver(function () {
      var e = document.getElementById('slidecarthq');
      if (e) { waiter.disconnect(); start(e); }
    });
    waiter.observe(document.documentElement, { childList: true, subtree: true });
    // FIX 19.08.2026: nicht ewig die ganze Seite beobachten, falls der Slide Cart
    // auf dieser Seite gar nicht existiert.
    setTimeout(function () { waiter.disconnect(); }, 60000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();